const _excluded = ["onClose", "onSave", "availableSettings", "testId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { useCallback, useMemo, useState } from 'react';
import produce from 'immer';
import { pathgen } from 'object-path-generator';
import { Typography, ConfirmationModal } from 'seagull';
import { useIoContext, useStoreActions } from "../../contexts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { renderAdvancedSettingControl } from "../ServiceAdvancedSettings/ServiceAdvancedSettings";
import { AdvancedSettingCategory, ADVANCED_SETTING_CATEGORY_MAP } from "../ServiceAdvancedSettings/ServiceAdvancedSettings.types";
import styles from "./ServiceAdvancedSettingsModal.module.scss";
import { createLocalActionContext } from "./ServiceAdvancedSettingsModal.utils";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const {
  ServiceAdvancedSettingsModalTestIds
} = pathgen();
export const ServiceAdvancedSettingsModal = _ref => {
  let {
      onClose,
      onSave,
      availableSettings,
      testId = 'service-advanced-settings-modal'
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const [localService, setLocalService] = useState(() => produce(rest.service, () => {}));
  const {
    logBI
  } = useStoreActions();
  const {
    getNow
  } = useIoContext();
  const now = useMemo(() => getNow(), [getNow]);
  const featureFlags = useFeatureFlags();
  const isApprovedRelationship = rest.relationshipState.type === 'approved';
  const effectiveDate = rest.relationshipState.effectiveDate;
  const updateLocalService = useCallback(updater => {
    setLocalService(prev => updater(prev));
  }, []);
  const localActionContext = useMemo(() => createLocalActionContext({
    localService,
    now,
    relationshipState: rest.relationshipState,
    effectiveDate,
    isApprovedRelationship,
    updateLocalService,
    featureFlags,
    logBI
  }), [localService, now, rest.relationshipState, effectiveDate, isApprovedRelationship, updateLocalService, featureFlags, logBI]);
  const settingsByCategory = useMemo(() => {
    const grouped = {
      [AdvancedSettingCategory.Pricing]: [],
      [AdvancedSettingCategory.Billing]: [],
      [AdvancedSettingCategory.Integrations]: []
    };
    availableSettings.forEach(setting => {
      const category = ADVANCED_SETTING_CATEGORY_MAP[setting.type];
      grouped[category].push(setting);
    });
    return grouped;
  }, [availableSettings]);
  const categories = [AdvancedSettingCategory.Pricing, AdvancedSettingCategory.Billing, AdvancedSettingCategory.Integrations];
  const renderControl = setting => {
    const controlProps = _objectSpread(_objectSpread(_objectSpread({}, rest), {}, {
      service: localService,
      testId: testId + '-modal'
    }, setting), {}, {
      onPausedChange: localActionContext.onPausedChange,
      onBillOnAcceptanceChange: localActionContext.onBillOnAcceptanceChange,
      onProratedChange: localActionContext.onProratedChange,
      onOptionalChange: localActionContext.onOptionalChange,
      onLimitBillingTimesChange: localActionContext.onLimitBillingTimesChange,
      onAutoPriceIncreaseChange: localActionContext.onAutoPriceIncreaseChange,
      onQboClassChange: localActionContext.onQboClassChange,
      onKarbonChange: localActionContext.onKarbonChange,
      onFinancialCentsChange: localActionContext.onFinancialCentsChange,
      onLimitedTimeDiscountOfferChange: localActionContext.onLimitedTimeDiscountOfferChange,
      onDiscountPeriodExpirationChange: localActionContext.onDiscountPeriodExpirationChange,
      onPreapprovedAmountChange: localActionContext.onPreapprovedAmountChange,
      onStartBillingAtChange: localActionContext.onStartBillingAtChange
    });
    return /*#__PURE__*/_jsx("div", {
      "data-testid": ServiceAdvancedSettingsModalTestIds.Setting(ADVANCED_SETTING_CATEGORY_MAP[setting.type], setting.type),
      children: renderAdvancedSettingControl(setting, controlProps, localService)
    }, setting.type);
  };
  return /*#__PURE__*/_jsx(ConfirmationModal, {
    title: "Advanced settings",
    onCloseButtonClick: onClose,
    "data-testid": ServiceAdvancedSettingsModalTestIds.Root(),
    primaryButtonLabel: "Save",
    secondaryButtonLabel: "Cancel",
    onPrimaryButtonClick: () => onSave(localService),
    onSecondaryButtonClick: onClose,
    width: "fit-content",
    content: /*#__PURE__*/_jsx(_Fragment, {
      children: categories.map(category => {
        const settings = settingsByCategory[category];
        if (settings.length === 0) return null;
        return /*#__PURE__*/_jsxs("div", {
          className: styles.category,
          "data-testid": ServiceAdvancedSettingsModalTestIds.Category(category),
          children: [/*#__PURE__*/_jsx(Typography, {
            variant: "paragraph1",
            skin: "secondary",
            children: category
          }), settings.map(setting => renderControl(setting))]
        }, category);
      })
    })
  });
};